import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Mail, Phone } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const COLUMNS = [
    {
        title: "Platform",
        color: "text-[#22D3EE]",
        links: ["Job Management", "Sales Pipeline", "AI Marketing Automation", "Finance Management", "Stock & Inventory", "AI Operations Assistant", "Management P&L", "HR & Staff Management"],
    },
    {
        title: "Solutions",
        color: "text-[#C084FC]",
        links: ["Stop Missed Enquiries", "Reduce Admin Workload", "Get Paid Faster", "Improve Customer Retention", "Recover Lost Sales", "Automate Follow-Ups", "Connect Online & Offline", "Understand Profitability"],
    },
    {
        title: "Resources",
        color: "text-[#22D3EE]",
        links: ["Insights & News", "AI Insights", "Guides", "Integrations", "Knowledge Centre", "Contact"],
    },
    {
        title: "Company",
        color: "text-[#C084FC]",
        links: ["About TedOps", "Partners", "Careers", "Privacy Policy", "Terms", "Cookie Policy"],
    },
];

const LINK_TARGETS = {
    "Privacy Policy": "/privacy-policy/",
    Terms: "/terms/",
    "Cookie Policy": "/cookie-policy/",
    Contact: "/contact/",
    "Insights & News": "/blog",
    Guides: "/guides",
    Careers: "/careers",
    "AI Insights": "/ai-insights",
    Integrations: "/integrations",
    "Knowledge Centre": "/knowledge-centre",
    "Job Management": "/platform/job-management",
    "Sales Pipeline": "/platform/sales-pipeline",
    "AI Marketing Automation": "/platform/ai-marketing-automation",
    "Finance Management": "/platform/finance-management",
    "Stock & Inventory": "/platform/stock-inventory",
    "AI Operations Assistant": "/platform/ai-operations-assistant",
    "Management P&L": "/platform/management-pnl",
    "HR & Staff Management": "/platform/hr-staff-management",
    "Stop Missed Enquiries": "/solutions/stop-missed-enquiries",
    "Reduce Admin Workload": "/solutions/reduce-admin-workload",
    "Get Paid Faster": "/solutions/get-paid-faster",
    "Improve Customer Retention": "/solutions/improve-customer-retention",
    "Recover Lost Sales": "/solutions/recover-lost-sales-opportunities",
    "Automate Follow-Ups": "/solutions/automate-customer-follow-ups",
    "Connect Online & Offline": "/solutions/connect-online-offline-sales",
    "Understand Profitability": "/solutions/understand-business-profitability",
};

const TOOLS = [
    { name: "Sage", slug: "sage" },
    { name: "QuickBooks", slug: "quickbooks" },
    { name: "WooCommerce", slug: "woocommerce" },
    { name: "Google Maps", slug: "googlemaps" },
    { name: "Twilio", slug: "twilio" },
    { name: "WhatsApp", slug: "whatsapp" },
];

const SOCIALS = [
    { name: "LinkedIn", slug: "linkedin" },
    { name: "X", slug: "x" },
    { name: "YouTube", slug: "youtube" },
    { name: "TikTok", slug: "tiktok" },
];

const ToolChip = ({ tool }) => {
    const [err, setErr] = useState(false);
    return (
        <span className="inline-flex items-center gap-2.5 rounded-xl border border-white/10 bg-[#0D1526]/80 px-4 py-2.5 text-xs font-medium text-[#CBD5E1] transition-colors duration-300 hover:border-[#00E5FF]/30 hover:text-white">
            {!err ? (
                <img
                    src={`https://cdn.simpleicons.org/${tool.slug}`}
                    alt={`${tool.name} logo`}
                    className="h-4 w-4 object-contain"
                    loading="lazy"
                    onError={() => setErr(true)}
                />
            ) : (
                <span className="flex h-4 w-4 items-center justify-center rounded bg-gradient-to-br from-[#0066FF] to-[#A855F7] text-[8px] font-bold text-white">
                    {tool.name[0]}
                </span>
            )}
            {tool.name}
        </span>
    );
};

const SocialIcon = ({ social }) => {
    const [err, setErr] = useState(false);
    return (
        <button
            type="button"
            aria-label={social.name}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-[#94A3B8] transition-all duration-300 hover:border-[#00E5FF]/40 hover:text-white"
            data-testid={`social-${social.slug}`}
        >
            {!err ? (
                <img
                    src={`https://cdn.simpleicons.org/${social.slug}/94A3B8`}
                    alt=""
                    className="h-4 w-4"
                    loading="lazy"
                    onError={() => setErr(true)}
                />
            ) : (
                <span className="text-xs font-bold">{social.name[0]}</span>
            )}
        </button>
    );
};

export default function Footer() {
    return (
        <footer
            className="relative overflow-hidden bg-[#0A0F1C]"
            style={{
                background:
                    "radial-gradient(circle at 85% 0%, rgba(0,102,255,0.12) 0%, transparent 40%), radial-gradient(circle at 0% 100%, rgba(168,85,247,0.08) 0%, transparent 45%), #0A0F1C",
            }}
            data-testid="site-footer"
        >
            <div className="relative mx-auto max-w-7xl px-6 pt-20 sm:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="grid gap-14 lg:grid-cols-[1fr_1.5fr] lg:gap-20"
                >
                    <div>
                        <img
                            src="/logo-wordmark.webp"
                            alt="TedOps AI"
                            className="h-10 w-auto select-none [filter:drop-shadow(0_0_18px_rgba(0,102,255,0.35))]"
                            draggable="false"
                            data-testid="footer-logo"
                        />
                        <h2 className="mt-8 font-display text-3xl font-bold leading-[1.15] tracking-tight text-[#F8FAFF] sm:text-4xl">
                            AI-powered operations
                            <br />
                            software for smarter
                            <br />
                            <span className="text-brand-gradient">service businesses.</span>
                        </h2>
                        <p className="mt-5 max-w-md text-[15px] leading-relaxed text-[#94A3B8]">
                            Connect enquiries, scheduling, field teams, stock and invoicing — all in
                            one intelligent platform built for growing service businesses.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a
                                href="mailto:enquires@tedopsai.com?subject=Book%20a%20TedOps%20AI%20demo"
                                className="group inline-flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-6 py-3.5 text-sm font-bold text-white shadow-[0_12px_30px_-10px_rgba(0,102,255,0.6)] transition-transform duration-300 hover:scale-[1.03]"
                                data-testid="footer-book-demo"
                            >
                                Book a Demo
                                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                            <button
                                type="button"
                                className="group inline-flex items-center gap-2.5 rounded-xl border border-[#0066FF]/40 px-6 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:border-[#00E5FF]/60"
                                data-testid="footer-see-platform"
                            >
                                See the Platform
                                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                        </div>
                    </div>

                    <div>
                        <div className="grid grid-cols-2 gap-10 sm:grid-cols-4 sm:gap-6">
                            {COLUMNS.map((col) => (
                                <nav key={col.title} aria-label={col.title}>
                                    <p className={`font-mono-brand text-[11px] font-medium uppercase tracking-[0.25em] ${col.color}`}>
                                        {col.title}
                                    </p>
                                    <ul className="mt-5 space-y-3.5">
                                        {col.links.map((link) => {
                                            const to = LINK_TARGETS[link];
                                            return (
                                                <li key={link}>
                                                    {to ? (
                                                        <Link
                                                            to={to}
                                                            className="text-[15px] text-[#94A3B8] transition-colors duration-200 hover:text-white"
                                                            data-testid={`footer-link-${link.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                        >
                                                            {link}
                                                        </Link>
                                                    ) : (
                                                        <span className="cursor-pointer text-[15px] text-[#94A3B8] transition-colors duration-200 hover:text-white">
                                                            {link}
                                                        </span>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </nav>
                            ))}
                        </div>

                        <div className="mt-12 flex flex-col gap-5 rounded-2xl border border-white/10 bg-[#0D1526]/50 p-6 sm:flex-row sm:items-center">
                            <p className="shrink-0 font-mono-brand text-[10px] uppercase leading-relaxed tracking-[0.2em] text-[#64748B]">
                                Integrates with
                                <br />
                                the tools you use
                            </p>
                            <div className="flex flex-wrap gap-3" data-testid="footer-tools">
                                {TOOLS.map((tool) => (
                                    <ToolChip key={tool.name} tool={tool} />
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>

                <div className="mt-16 flex flex-col gap-8 border-t border-white/10 py-9 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col gap-8 sm:flex-row sm:gap-16">
                        <a href="mailto:sales@tedops.ai" className="group flex items-center gap-4" data-testid="footer-email">
                            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-[#0D1526] transition-colors duration-300 group-hover:border-[#00E5FF]/40">
                                <Mail className="h-5 w-5 text-[#22D3EE]" strokeWidth={1.75} />
                            </span>
                            <span>
                                <span className="block text-[15px] font-bold text-[#F8FAFF]">sales@tedops.ai</span>
                                <span className="block text-xs text-[#64748B]">Get in touch with our team</span>
                            </span>
                        </a>
                        <a href="tel:+442045870214" className="group flex items-center gap-4" data-testid="footer-phone">
                            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-[#0D1526] transition-colors duration-300 group-hover:border-[#00E5FF]/40">
                                <Phone className="h-5 w-5 text-[#22D3EE]" strokeWidth={1.75} />
                            </span>
                            <span>
                                <span className="block text-[15px] font-bold text-[#F8FAFF]">+44 20 4587 0214</span>
                                <span className="block text-xs text-[#64748B]">Mon – Fri, 9am – 5pm (GMT)</span>
                            </span>
                        </a>
                    </div>
                    <div className="flex gap-3" data-testid="footer-socials">
                        {SOCIALS.map((s) => (
                            <SocialIcon key={s.slug} social={s} />
                        ))}
                    </div>
                </div>

                <div className="flex flex-col items-start justify-between gap-4 border-t border-white/10 py-7 sm:flex-row sm:items-center">
                    <p className="text-xs text-[#64748B]">© 2026 TedOps AI. All rights reserved.</p>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-[#94A3B8]" data-testid="footer-legal">
                        <Link to="/privacy-policy/" className="inline-flex min-h-[44px] items-center transition-colors hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
                        <span aria-hidden="true" className="h-3 w-px bg-white/15" />
                        <Link to="/terms/" className="inline-flex min-h-[44px] items-center transition-colors hover:text-white" data-testid="footer-terms-link">Terms and Conditions</Link>
                        <span aria-hidden="true" className="h-3 w-px bg-white/15" />
                        <Link to="/cookie-policy/" className="inline-flex min-h-[44px] items-center transition-colors hover:text-white" data-testid="footer-cookies-link">Cookies</Link>
                        <span aria-hidden="true" className="h-3 w-px bg-white/15" />
                        <button
                            type="button"
                            onClick={() => window.dispatchEvent(new Event("tedops:open-cookie-settings"))}
                            className="inline-flex min-h-[44px] items-center transition-colors hover:text-white"
                            data-testid="footer-cookie-settings"
                        >
                            Cookie Settings
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
}
